import React, { useState, useEffect, useMemo } from "react";
import { IoArrowBack, IoOpenOutline, IoSearchOutline } from "react-icons/io5";
import { HiOutlineDownload } from "react-icons/hi";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { useEtatGlobal } from "../EtatGlobal.js";

const STATUS_COLORS = {
  ACTIF: "#22c55e",
  INACTIF: "#ef4444",
  SUSPENDU: "#f59e0b",
};

const EMPTY_FORM = { name: "", city: "", address: "", postalCode: "", phone: "", responsableId: "" };

function Cabinets({ onCancel }) {
  const { users } = useEtatGlobal();
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingCabinet, setEditingCabinet] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        `${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`)
      ),
    [users]
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await httpClient.get("https://backend-myalfa.vercel.app/api/cabinets");
      setCabinets(data);
    } catch (err) {
      console.error("Erreur chargement cabinets :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredCabinets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cabinets;
    return cabinets.filter((cab) =>
      [cab.name, cab.city, cab.address]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [cabinets, search]);

  const getResponsable = (cab) =>
    cab.userCabinets?.find((uc) => uc.isResponsible)?.user || null;

  const openCreate = () => {
    setEditingId(null);
    setEditingCabinet(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (cab) => {
    const responsable = getResponsable(cab);
    setEditingId(cab.id);
    setEditingCabinet(cab);
    setForm({
      name: cab.name,
      city: cab.city || "",
      address: cab.address || "",
      postalCode: cab.postalCode || "",
      phone: cab.phone || "",
      responsableId: responsable ? String(responsable.id) : "",
    });
    setError(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingCabinet(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const applyResponsable = async (cabinetId, newResponsableId, currentCabinet) => {
    const previousResponsable = currentCabinet ? getResponsable(currentCabinet) : null;
    const previousId = previousResponsable ? String(previousResponsable.id) : "";
    if (newResponsableId === previousId) return;

    if (previousId && previousId !== newResponsableId) {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/cabinets/${cabinetId}/users/${previousId}`,
        { isResponsible: false }
      );
    }

    if (newResponsableId) {
      const alreadyLinked = currentCabinet?.userCabinets?.some(
        (uc) => String(uc.user.id) === newResponsableId
      );
      if (alreadyLinked) {
        await httpClient.put(
          `https://backend-myalfa.vercel.app/api/cabinets/${cabinetId}/users/${newResponsableId}`,
          { isResponsible: true }
        );
      } else {
        await httpClient.post(
          `https://backend-myalfa.vercel.app/api/cabinets/${cabinetId}/users`,
          { userId: newResponsableId, isResponsible: true }
        );
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const cabinetPayload = {
        name: form.name,
        city: form.city,
        address: form.address,
        postalCode: form.postalCode,
        phone: form.phone,
      };

      let cabinetId = editingId;
      let currentCabinet = editingCabinet;
      if (editingId) {
        await httpClient.put(
          `https://backend-myalfa.vercel.app/api/cabinets/${editingId}`,
          cabinetPayload
        );
      } else {
        const { data } = await httpClient.post(
          "https://backend-myalfa.vercel.app/api/cabinets",
          cabinetPayload
        );
        cabinetId = data.id;
        currentCabinet = data;
        // Le cabinet existe déjà côté serveur à partir d'ici : si l'assignation
        // du responsable échoue ci-dessous, un nouveau clic sur "Créer" doit
        // mettre à jour ce cabinet plutôt que d'en créer un doublon.
        setEditingId(cabinetId);
        setEditingCabinet(data);
      }

      if (currentCabinet || form.responsableId) {
        await applyResponsable(cabinetId, form.responsableId, currentCabinet);
      }

      cancelForm();
      load();
    } catch (err) {
      console.error("Erreur sauvegarde cabinet :", err);
      setError("Une erreur est survenue.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!(await dialog.confirm("Supprimer ce cabinet ?"))) return;
    try {
      await httpClient.delete(`https://backend-myalfa.vercel.app/api/cabinets/${editingId}`);
      cancelForm();
      load();
    } catch (err) {
      console.error("Erreur suppression cabinet :", err);
    }
  };

  const handleExport = () => {
    const header = ["Cabinet", "Adresse", "Ville", "Téléphone", "Responsable", "Collaborateurs", "Clients"];
    const rows = filteredCabinets.map((cab) => {
      const responsable = getResponsable(cab);
      return [
        cab.name,
        cab.address || "",
        cab.city || "",
        cab.phone || "",
        responsable ? `${responsable.name} ${responsable.lastName}` : "",
        cab.userCabinets?.length || 0,
        cab._count?.clients || 0,
      ];
    });
    const csv = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cabinets.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-[#840040] transition-colors flex-shrink-0"
        >
          <IoArrowBack size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Liste des cabinets</h1>
        <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
          {cabinets.length}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-6 ml-8">Sites du groupe ALFA</p>

      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="relative w-full max-w-md">
          <IoSearchOutline
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un cabinet..."
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
          />
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <HiOutlineDownload size={16} />
            Exporter
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors"
          >
            + Ajouter un cabinet
          </button>
        </div>
      </div>

      {/* Modal ajout / modification */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center"
          onClick={cancelForm}
        >
          <div
            className="bg-white rounded-2xl p-7 w-[440px] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              {editingId ? "Modifier le cabinet" : "Nouveau cabinet"}
            </h4>
            <form onSubmit={handleSave} className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Nom *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ex : ALFA Le Tampon"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Adresse</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Ex : 8 rue Hubert de Lisle"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Ville</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                  placeholder="Ex : Le Tampon"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Code postal</label>
                <input
                  type="text"
                  value={form.postalCode}
                  onChange={(e) => setForm((p) => ({ ...p, postalCode: e.target.value }))}
                  placeholder="Ex : 97430"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Téléphone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Ex : 0262 27 04 18"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Responsable</label>
                <select
                  value={form.responsableId}
                  onChange={(e) => setForm((p) => ({ ...p, responsableId: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                >
                  <option value="">-- Aucun responsable --</option>
                  {sortedUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <div className="flex items-center justify-between mt-3">
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? "Enregistrement..." : editingId ? "Modifier" : "Créer"}
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg border border-[#840040] text-[#840040] text-sm font-semibold hover:bg-[#840040]/5 transition-colors"
                    type="button"
                    onClick={cancelForm}
                  >
                    Annuler
                  </button>
                </div>
                {editingId && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : filteredCabinets.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-10">
          {cabinets.length === 0 ? "Aucun cabinet enregistré." : "Aucun cabinet trouvé."}
        </p>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#fff6ee] text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                <th className="px-5 py-3">Cabinet</th>
                <th className="px-5 py-3">Adresse</th>
                <th className="px-5 py-3">Ville</th>
                <th className="px-5 py-3">CP</th>
                <th className="px-5 py-3">Téléphone</th>
                <th className="px-5 py-3">Responsable</th>
                <th className="px-5 py-3 text-right">Collab.</th>
                <th className="px-5 py-3 text-right">Clients</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCabinets.map((cab) => {
                const responsable = getResponsable(cab);
                return (
                  <tr key={cab.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span
                          className="w-1 h-8 rounded-full flex-shrink-0"
                          style={{ backgroundColor: STATUS_COLORS[cab.status] || "#9ca3af" }}
                        />
                        <span className="font-semibold text-gray-900">{cab.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{cab.address || "—"}</td>
                    <td className="px-5 py-4 text-gray-600">{cab.city || "—"}</td>
                    <td className="px-5 py-4 text-gray-600">{cab.postalCode || "—"}</td>
                    <td className="px-5 py-4 text-gray-600">{cab.phone || "—"}</td>
                    <td className="px-5 py-4">
                      {responsable ? (
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#5C1A30]/10 flex items-center justify-center text-[11px] font-bold text-[#5C1A30] flex-shrink-0 uppercase">
                            {responsable.name?.[0]}
                            {responsable.lastName?.[0]}
                          </div>
                          <span className="text-gray-700">
                            {responsable.name} {responsable.lastName}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-[#840040]">
                      {cab.userCabinets?.length || 0}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-[#840040]">
                      {cab._count?.clients || 0}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => openEdit(cab)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-[#840040] hover:text-[#840040] transition-colors whitespace-nowrap"
                      >
                        <IoOpenOutline size={13} />
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Cabinets;
